import type React from "react";
import { useEffect, useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { ImageDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { ChevronLeftIcon } from "@/components/Icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { ImagePlaceholder } from "@/components/ImagePlaceholder/ImagePlaceholder";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { preloadImage } from "@/helpers/image";
import { isDefined } from "@/helpers/util";
import { useBool } from "@/hooks/useBool";
import { useKey } from "@/hooks/useKey";
import { useTranslation } from "@/translations";
import { RuntimeCarouselTrack } from "@/ui-adapters/RuntimeCarouselTrack";

const SWIPE_THRESHOLD = 40;

type CarouselImage = {
  url: string;
  description?: string;
};

type CarouselProps = {
  images: CarouselImage[];
  defaultPage?: number;
  objectFit?: "contain" | "cover";
} & (
  | {
      variant: "overlay";
      allowZoom?: never;
    }
  | {
      variant?: "default";
      allowZoom?: boolean;
    }
);

export function Carousel({
  images,
  defaultPage,
  objectFit = "cover",
  allowZoom,
  variant = "default",
}: CarouselProps): React.ReactNode {
  const [isZoomModalOpened, zoomModalHandler] = useBool();
  const [activePage, setActivePage] = useState(defaultPage ?? 0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const pointerStartX = useRef<number | undefined>(undefined);
  const didSwipe = useRef(false);

  const { t } = useTranslation();
  const canShowPrevious = activePage > 0;
  const canShowNext = activePage < images.length - 1;
  const activeImage = images[activePage];

  // Preload next slide
  useEffect(() => {
    const nextImage = images[(activePage || 0) + 1];

    if (nextImage) {
      preloadImage(nextImage.url);
    }
  }, [activePage, images]);

  useKey("Escape", zoomModalHandler.setFalse);
  useKey("ArrowLeft", () => setActivePage((page) => Math.max(0, page - 1)), variant === "overlay" && canShowPrevious);
  useKey(
    "ArrowRight",
    () => setActivePage((page) => Math.min(images.length - 1, page + 1)),
    variant === "overlay" && canShowNext,
  );

  if (!activeImage) {
    return null;
  }

  function handlePointerDown(event: React.PointerEvent<HTMLElement>): void {
    if (!event.isPrimary) {
      return;
    }

    pointerStartX.current = event.clientX;
    didSwipe.current = false;
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLElement>): void {
    const startX = pointerStartX.current;
    if (!event.isPrimary || startX === undefined) {
      return;
    }

    const distance = event.clientX - startX;
    const isPastStart = activePage === 0 && distance > 0;
    const isPastEnd = activePage === images.length - 1 && distance < 0;
    setDragOffset(isPastStart || isPastEnd ? distance / 3 : distance);
  }

  function handlePointerUp(event: React.PointerEvent<HTMLElement>): void {
    const startX = pointerStartX.current;
    pointerStartX.current = undefined;
    setDragOffset(0);
    setIsDragging(false);

    if (!event.isPrimary || startX === undefined) {
      return;
    }

    const distance = event.clientX - startX;
    if (Math.abs(distance) < SWIPE_THRESHOLD) {
      return;
    }

    didSwipe.current = true;
    setActivePage((page) => (distance > 0 ? Math.max(0, page - 1) : Math.min(images.length - 1, page + 1)));
  }

  function handlePointerCancel(): void {
    pointerStartX.current = undefined;
    setDragOffset(0);
    setIsDragging(false);
  }

  function handleImageClick(): void {
    if (didSwipe.current) {
      didSwipe.current = false;

      return;
    }

    zoomModalHandler.setTrue();
  }

  function handleImageKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    handleImageClick();
  }

  return (
    <>
      <section
        role="region"
        aria-roledescription="carousel"
        className="relative size-full touch-pan-y overflow-hidden select-none"
      >
        <RuntimeCarouselTrack page={activePage} dragOffset={dragOffset}>
          {(trackAttributes) => (
            <div
              {...trackAttributes}
              className={twJoin(
                "flex size-full ease-out will-change-transform",
                !isDragging && "transition-transform motion-reduce:transition-none",
              )}
            >
              {images.map((image, index) => (
                <div
                  role="group"
                  aria-hidden={index !== activePage}
                  aria-label={t("component.carousel.slide-position", {
                    current: index + 1,
                    total: images.length,
                  })}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerCancel}
                  key={`${image.url}-${index}`}
                  className={twJoin(
                    "relative size-full shrink-0 bg-background-default-inverse",
                    allowZoom && "cursor-zoom-in",
                  )}
                >
                  {allowZoom ? (
                    <div
                      role="button"
                      aria-label={t("component.gallery.zoom-in-image")}
                      tabIndex={index === activePage ? 0 : -1}
                      onClick={handleImageClick}
                      onKeyDown={handleImageKeyDown}
                      className="relative size-full cursor-zoom-in"
                    >
                      <CarouselImageView image={image} objectFit={objectFit} presentation="slide" />
                    </div>
                  ) : (
                    <CarouselImageView image={image} objectFit={objectFit} presentation="slide" />
                  )}
                </div>
              ))}
            </div>
          )}
        </RuntimeCarouselTrack>

        <div className="absolute top-1/2 left-0 z-raised flex w-full -translate-y-1/2 items-center justify-between px-card-compact">
          <IconButton
            title={t("common.action.previous")}
            size="sm"
            icon={<ChevronLeftIcon />}
            withTooltip={false}
            variant="filled"
            disabled={!canShowPrevious}
            onClick={() => setActivePage((page) => Math.max(0, page - 1))}
            isCircular
          />
          <IconButton
            title={t("common.action.next")}
            icon={<ChevronRightIcon />}
            size="sm"
            withTooltip={false}
            variant="filled"
            disabled={!canShowNext}
            onClick={() => setActivePage((page) => Math.min(images.length - 1, page + 1))}
            isCircular
          />
        </div>

        <footer className="pointer-events-none absolute inset-x-0 bottom-0 z-raised flex justify-center p-space-sm">
          <nav
            aria-label={t("component.carousel.slide-navigation")}
            className="pointer-events-auto flex items-center rounded-full bg-background-default-inverse/60 px-space-xs shadow-raised backdrop-blur-md"
          >
            {images.map((_, index) => (
              <button
                aria-current={index === activePage ? "true" : undefined}
                aria-label={t("component.carousel.go-to-slide", { slide: index + 1 })}
                onClick={() => setActivePage(index)}
                key={index}
                type="button"
                className="group flex size-control-sm shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
              >
                <span
                  aria-hidden
                  className={twJoin(
                    "size-status-dot shrink-0 rounded-full transition-colors group-hover:bg-background-default/80",
                    index === activePage ? "bg-background-default" : "bg-background-default/50",
                  )}
                />
              </button>
            ))}
          </nav>
        </footer>
      </section>
      {allowZoom && (
        <ImageModal
          images={images as FormImage[]}
          zoomedImageIndex={activePage}
          isOpened={isZoomModalOpened}
          onOpenChange={zoomModalHandler.set}
        />
      )}
    </>
  );
}

// In the same file, because it's a circular dependency (Carousel -> ImageModal -> Carousel)
type ImageModalProps = ModalBaseProps & {
  images: FormImage[];
  zoomedImageIndex: number | undefined;
};

export function ImageModal({ isOpened, onOpenChange, images, zoomedImageIndex }: ImageModalProps): React.ReactNode {
  const zoomedImage = isDefined(zoomedImageIndex) ? images[zoomedImageIndex] : undefined;

  return (
    <Modal.Root isNested {...{ isOpened, onOpenChange }}>
      <Modal.Body>
        <figure className="aspect-3/4 w-full overflow-hidden md:aspect-square">
          {images.length === 1 && zoomedImage && (
            <div className="size-full overflow-hidden rounded-lg bg-background-default-inverse">
              <CarouselImageView image={zoomedImage} objectFit="contain" presentation="modal" />
            </div>
          )}
          {images.length > 1 && (
            <div className="size-full overflow-hidden rounded-lg">
              <Carousel defaultPage={zoomedImageIndex} variant="overlay" objectFit="contain" {...{ images }} />
            </div>
          )}
        </figure>
      </Modal.Body>
    </Modal.Root>
  );
}

function CarouselImageView({
  image,
  objectFit,
  presentation,
}: {
  image: CarouselImage;
  objectFit: "contain" | "cover";
  presentation: "slide" | "modal";
}): React.ReactNode {
  const [isLoading, setIsLoading] = useState(true);
  const uploadedImage = isImageDto(image) ? image : undefined;

  return (
    <>
      {isLoading && <ImagePlaceholder image={uploadedImage} objectFit={objectFit} />}
      <img
        className={twJoin(
          presentation === "slide" ? "absolute inset-y-0 size-full" : "size-full",
          objectFit === "contain" ? "object-contain" : "object-cover",
        )}
        src={image.url}
        alt={image.description ?? ""}
        draggable={false}
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
      />
    </>
  );
}

function isImageDto(image: CarouselImage): image is ImageDto {
  return "id" in image && "metadata" in image;
}
